import { Button } from 'designSystem/components/Button'
import {
  COMPARISON_COLORS,
  COMPARISON_SEVERITY,
} from 'features/labeler/comparison'
import { useLabeler, useLabelerOptional } from 'features/labeler/LabelerContext'
import { colorForBox, typeLabel, typesByKind } from 'features/labeler/taxonomy'
import type { LabeledBox } from 'features/labeler/types'
import { useLabelerSiteStats } from 'features/labeler/useLabelerService'
import { Trash2 } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

const DrawerContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 26rem;
  min-width: 26rem;
  height: 100%;
  border-left: 1px solid ${({ theme }) => theme.forecast.border};
  background: ${({ theme }) => theme.forecast.background1};
  overflow: hidden;
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
`

const HeaderTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
`

const Section = styled.div`
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`

const Muted = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
`

const Mono = styled.span`
  font-family: monospace;
  font-size: 0.78rem;
`

const Select = styled.select`
  font-size: 0.78rem;
  padding: 0.15rem 0.25rem;
  max-width: 100%;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.forecast.background1};
  color: inherit;
`

const TextInput = styled.input`
  font-size: 0.78rem;
  padding: 0.2rem 0.35rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.forecast.background1};
  color: inherit;
  flex: 1;
  min-width: 0;
`

const BoxList = styled.div`
  flex: 1;
  overflow-y: auto;
`

const BoxRowContainer = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0.3rem 0.75rem 0.3rem 0.6rem;
  gap: 0.35rem;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border2};
  border-left: 2px solid
    ${({ $selected }) =>
      $selected ? 'rgba(59, 130, 246, 0.9)' : 'transparent'};
  background: ${({ $selected }) =>
    $selected ? 'rgba(37, 99, 235, 0.1)' : 'transparent'};
`

const Swatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  min-width: 10px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  display: inline-block;
`

const DescriptionLine = styled.div`
  font-size: 0.68rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin-left: 2.05rem;
  margin-top: -0.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ColorDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  display: inline-block;
`

/** A small green check square — progress tracking, not selection.
 * Clicking never selects the row. */
const VerifiedCheck = styled.button<{ $on: boolean }>`
  width: 15px;
  height: 15px;
  min-width: 15px;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  border: 1px solid
    ${({ $on }) =>
      $on ? 'rgba(34, 197, 94, 0.6)' : 'rgba(107, 114, 128, 0.7)'};
  background: ${({ $on }) => ($on ? 'rgba(22, 163, 74, 0.2)' : 'transparent')};
  color: #4ade80;
`

const TypeChip = styled.span<{ $color: string; $fill: string }>`
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.05rem 0.3rem;
  border-radius: 4px;
  white-space: nowrap;
  border: 1px solid ${({ $color }) => $color};
  color: ${({ $color }) => $color};
  background: ${({ $fill }) => $fill};
`

const TrashButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 0.1rem;
  color: inherit;
  opacity: 0.4;
  display: inline-flex;
  &:hover {
    opacity: 1;
    color: #ef4444;
  }
`

const ProgressTrack = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${({ theme }) => theme.forecast.border2};
  overflow: hidden;
`

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: #22c55e;
  transition: width 0.15s ease;
`

const IconButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0 0.2rem;
  color: inherit;
  &:hover {
    opacity: 0.7;
  }
`

const TinyCheck = styled.input`
  &&& {
    appearance: auto;
    width: 13px;
    height: 13px;
    min-width: 13px;
    min-height: 13px;
    margin: 0;
    padding: 0;
    accent-color: #22c55e;
    cursor: pointer;
  }
`

const Footer = styled.div`
  padding: 0.5rem 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const KeyHint = styled.span`
  font-family: monospace;
  font-size: 0.68rem;
  background: ${({ theme }) => theme.forecast.border2};
  border-radius: 3px;
  padding: 0 0.25rem;
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 3.2rem repeat(3, 1fr);
  gap: 0.1rem 0.5rem;
  font-size: 0.75rem;
`

const CategoryChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  background: ${({ $active, theme }) =>
    $active ? theme.forecast.background2 : 'transparent'};
  color: inherit;
`

const ComparisonRow = styled.div<{ $selected: boolean; $color: string }>`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.3rem 0.75rem 0.3rem 0.55rem;
  cursor: pointer;
  border-left: 3px solid ${({ $color }) => $color};
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border2};
  background: ${({ $selected, theme }) =>
    $selected ? `${theme.forecast.background2}` : 'transparent'};
`

const HelpPanel = styled.div`
  position: absolute;
  top: 2.6rem;
  right: 0.5rem;
  z-index: 30;
  width: 22rem;
  max-height: 70%;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.background1};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  padding: 0.6rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`

const HelpGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`

const HelpRow = styled.div`
  display: flex;
  gap: 0.5rem;
  font-size: 0.72rem;
  align-items: baseline;
`

const HELP_SECTIONS: Array<{ title: string; rows: Array<[string, string]> }> = [
  {
    title: 'Navigation',
    rows: [
      ['j / ↓', 'Next box in the filtered list (wraps, zooms to it)'],
      ['k / ↑', 'Previous box (wraps, zooms)'],
      ['0', 'Reset zoom to the fitted view'],
      [
        'Esc',
        'Deselect everything; cancel an in-flight drag; exit a text field',
      ],
    ],
  },
  {
    title: 'Selection',
    rows: [
      ['Click box / row', 'Select (row click also zooms to the box)'],
      ['Shift+Click', 'Toggle in/out of the bulk selection'],
      ['Shift+Drag (canvas)', 'Rubber-band bulk select — always adds'],
      ['a', 'Select all visible (filtered) boxes'],
    ],
  },
  {
    title: 'Editing',
    rows: [
      [
        'v',
        'Toggle verified — bulk toggles each; under the Unconfirmed filter it auto-advances',
      ],
      [
        'd / Delete / ⌫',
        'Delete the selection (bulk if any, else the single selection)',
      ],
      [
        'b',
        'Arm Draw Mode, then Shift+Drag draws (snaps to OCR + prefills text)',
      ],
      ['Drag edge', 'Resize the selected box'],
      ['Shift+Drag inside', 'Move the selected box'],
      ['c', 'Focus the type picker (bulk bar first, else the open row)'],
      ['x', 'Remove the description from the selection (bulk if any)'],
      ['e', 'Focus the selected row\u2019s text field'],
      ['g', 'Group ≥2 selected boxes (join texts, union bbox)'],
    ],
  },
  {
    title: 'Syncing',
    rows: [
      [
        'Ctrl/Cmd+S',
        'Sync now (edits also sync automatically ~20s after you stop)',
      ],
    ],
  },
]

const pct = (v: number) => `${(v * 100).toFixed(1)}%`

const SyncDot = styled.span<{ $state: string }>`
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  display: inline-block;
  background: ${({ $state }) =>
    $state === 'error'
      ? '#ef4444'
      : $state === 'dirty'
        ? '#fb923c'
        : $state === 'syncing'
          ? '#3b82f6'
          : $state === 'synced'
            ? '#22c55e'
            : '#6b7280'};
`

const UNTYPED_SENTINEL = '__untyped__'

function TypePicker({
  value,
  onChange,
  dataAttr,
}: {
  value: string | null
  onChange: (type: string | null) => void
  dataAttr?: string
}) {
  const { taxonomy } = useLabeler()
  const grouped = useMemo(() => typesByKind(taxonomy), [taxonomy])
  const attrs = dataAttr ? { [dataAttr]: true } : {}
  return (
    <Select
      {...attrs}
      value={value ?? ''}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        if (e.target.value === UNTYPED_SENTINEL) onChange(null)
        else if (e.target.value) onChange(e.target.value)
      }}>
      <option value='' disabled>
        Pick a type…
      </option>
      <option value={UNTYPED_SENTINEL}>
        Untyped (type not captured yet — kept local, never published)
      </option>
      {Object.entries(grouped).map(([kind, types]) => (
        <optgroup key={kind} label={typeLabel(kind)}>
          {types.map((type) => (
            <option key={type} value={type}>
              {typeLabel(type)}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  )
}

function BoxRow({
  box,
  showDescriptions,
}: {
  box: LabeledBox
  showDescriptions: boolean
}) {
  const labeler = useLabeler()
  const {
    selectedBoxId,
    selectBox,
    selectedBoxIds,
    setSelectedBoxIds,
    update,
    remove,
    toggleBoxVerified,
    clearDescription,
  } = labeler
  const isSelected = box.id === selectedBoxId
  const isBulkSelected = selectedBoxIds.has(box.id)
  const rowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isSelected) {
      rowRef.current?.scrollIntoView({ block: 'nearest' })
    }
  }, [isSelected])

  const color = colorForBox(box)
  const chipColor = box.type
    ? { color: 'rgba(74, 222, 128, 0.95)', fill: 'rgba(34, 197, 94, 0.12)' }
    : { color: 'rgba(250, 204, 21, 0.95)', fill: 'rgba(234, 179, 8, 0.12)' }

  return (
    <BoxRowContainer
      ref={rowRef}
      $selected={isSelected || isBulkSelected}
      data-labeler-box-row={box.id}
      onClick={(e) => {
        if (e.shiftKey) {
          setSelectedBoxIds((cur) => {
            const next = new Set(cur)
            if (next.has(box.id)) next.delete(box.id)
            else next.add(box.id)
            return next
          })
        } else {
          selectBox(box.id)
        }
      }}>
      <Row style={{ flexWrap: 'nowrap' }}>
        <VerifiedCheck
          $on={box.verified}
          title={
            box.verified
              ? 'Verified — click to unverify'
              : 'Unverified — click to mark as verified'
          }
          onClick={(e) => {
            e.stopPropagation()
            toggleBoxVerified(box.id)
          }}>
          {box.verified ? '✓' : ''}
        </VerifiedCheck>
        <ColorDot $color={color.stroke} />
        <Mono style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {box.text || <Muted>(no text)</Muted>}
        </Mono>
        <TypeChip $color={chipColor.color} $fill={chipColor.fill}>
          {(box.type ? typeLabel(box.type) : 'Untyped').slice(0, 8)}
        </TypeChip>
        <TrashButton
          title='Delete extraction (d)'
          onClick={(e) => {
            e.stopPropagation()
            remove(new Set([box.id]))
          }}>
          <Trash2 size={13} strokeWidth={1.75} />
        </TrashButton>
      </Row>
      {showDescriptions && box.name && !isSelected && (
        <DescriptionLine>{box.name}</DescriptionLine>
      )}
      {isSelected && (
        <>
          <Row>
            <TextInput
              data-labeler-box-text='true'
              value={box.text}
              placeholder='Tag text'
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => update(box.id, { text: e.target.value })}
            />
            <TypePicker
              value={box.type}
              dataAttr='data-labeler-type-picker'
              onChange={(type) => update(box.id, { type })}
            />
          </Row>
          <Row>
            <TextInput
              value={box.name ?? ''}
              placeholder='Descriptive name (optional)'
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => update(box.id, { name: e.target.value || null })}
            />
            {box.name && (
              <IconButton
                title='Remove description (x)'
                onClick={(e) => {
                  e.stopPropagation()
                  clearDescription(box.id)
                }}>
                ✕
              </IconButton>
            )}
          </Row>
        </>
      )}
    </BoxRowContainer>
  )
}

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  )
}

export function LabelerDrawer(props: {
  onClose: () => void
  onResetZoom: () => void
}) {
  // Without a provider, render nothing rather than trip the tab's error
  // boundary.
  const labeler = useLabelerOptional()
  if (!labeler) return null
  return <LabelerDrawerInner {...props} labeler={labeler} />
}

function LabelerDrawerInner({
  onClose,
  onResetZoom,
  labeler,
}: {
  onClose: () => void
  onResetZoom: () => void
  labeler: ReturnType<typeof useLabeler>
}) {
  const {
    siteId,
    pageId,
    pageMeta,
    predictions,
    isLoadingPredictions,
    predictionsError,
    runs,
    seedSource,
    setSeedSource,
    seedLists,
    page,
    workingCopy,
    isSeeded,
    seed,
    confirmAll,
    retypeSelected,
    clear,
    selectedBoxId,
    selectBox,
    selectedBoxIds,
    setSelectedBoxIds,
    visibleBoxes,
    boxTextFilter,
    setBoxTextFilter,
    boxTypeFilter,
    setBoxTypeFilter,
    boxVerifiedFilter,
    setBoxVerifiedFilter,
    boxDescFilter,
    setBoxDescFilter,
    isDrawingMode,
    setIsDrawingMode,
    taxonomy,
    groupSelected,
    toggleBoxVerified,
    toggleBoxVerifiedMany,
    syncStatus,
    syncNow,
    isHydrating,
    compareMode,
    setCompareMode,
    iouThreshold,
    setIouThreshold,
    compareVerifiedOnly,
    setCompareVerifiedOnly,
    compareCategoryFilter,
    setCompareCategoryFilter,
    comparison,
    selectedComparisonId,
    selectComparisonItem,
  } = labeler
  const [groupDelimiter, setGroupDelimiter] = useState<string | null>(null)
  const [showDescriptions, setShowDescriptions] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const { data: siteStats } = useLabelerSiteStats(siteId)

  const boxes = page?.boxes ?? []
  const untypedCount = boxes.filter((b) => !b.type).length
  const unverifiedCount = boxes.filter((b) => !b.verified).length

  const sitePages = useMemo(
    () => Object.values(workingCopy).filter((p) => p.siteId === siteId),
    [workingCopy, siteId]
  )
  const labeledBoxCount = sitePages.reduce((n, p) => n + p.boxes.length, 0)

  const stepSelection = useCallback(
    (delta: number) => {
      if (visibleBoxes.length === 0) return
      const idx = visibleBoxes.findIndex((b) => b.id === selectedBoxId)
      const next =
        idx < 0
          ? delta > 0
            ? 0
            : visibleBoxes.length - 1
          : (idx + delta + visibleBoxes.length) % visibleBoxes.length
      selectBox(visibleBoxes[next].id)
    },
    [visibleBoxes, selectedBoxId, selectBox]
  )

  /** Confirming under the Unconfirmed filter removes the row from the list,
   * so compute the next row BEFORE toggling and land on it — otherwise every
   * keypress orphans the selection. */
  const toggleVerifiedSelected = useCallback(() => {
    if (!selectedBoxId) return
    if (boxVerifiedFilter === 'unverified') {
      const idx = visibleBoxes.findIndex((b) => b.id === selectedBoxId)
      const next =
        visibleBoxes[idx + 1]?.id ??
        (idx > 0 ? visibleBoxes[idx - 1]?.id : undefined)
      toggleBoxVerified(selectedBoxId)
      if (next) selectBox(next)
      return
    }
    toggleBoxVerified(selectedBoxId)
  }, [
    selectedBoxId,
    boxVerifiedFilter,
    visibleBoxes,
    toggleBoxVerified,
    selectBox,
  ])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault()
        if (labeledBoxCount > 0) void syncNow()
        return
      }
      if (isTypingTarget(e.target)) {
        if (e.key === 'Escape' && e.target instanceof HTMLElement) {
          e.target.blur()
        }
        return
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (compareMode) {
        if (e.key === '0') {
          e.preventDefault()
          onResetZoom()
        } else if (e.key === 'Escape') {
          selectComparisonItem(null, { zoom: false })
        }
        return
      }
      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          if (e.key === 'ArrowDown' && !isSeeded) return
          e.preventDefault()
          stepSelection(1)
          break
        case 'k':
        case 'ArrowUp':
          if (e.key === 'ArrowUp' && !isSeeded) return
          e.preventDefault()
          stepSelection(-1)
          break
        case 'v':
        case 'V':
          if (selectedBoxIds.size > 0) {
            e.preventDefault()
            toggleBoxVerifiedMany(selectedBoxIds)
          } else if (selectedBoxId) {
            e.preventDefault()
            toggleVerifiedSelected()
          }
          break
        case 'g':
        case 'G':
          if (selectedBoxIds.size >= 2) {
            e.preventDefault()
            setGroupDelimiter((cur) => (cur === null ? ' ' : cur))
          }
          break
        case 'd':
        case 'D':
        case 'Delete':
        case 'Backspace': {
          const ids =
            selectedBoxIds.size > 0
              ? selectedBoxIds
              : selectedBoxId
                ? new Set([selectedBoxId])
                : null
          if (ids) {
            e.preventDefault()
            labeler.remove(ids)
          }
          break
        }
        case 'a':
        case 'A':
          e.preventDefault()
          setSelectedBoxIds(new Set(visibleBoxes.map((b) => b.id)))
          break
        case 'x':
        case 'X':
          if (selectedBoxIds.size > 0 || selectedBoxId) {
            e.preventDefault()
            labeler.clearDescriptionsSelected()
          }
          break
        case 'b':
        case 'B':
          e.preventDefault()
          setIsDrawingMode(!isDrawingMode)
          break
        case 'c':
        case 'C': {
          const picker = document.querySelector<HTMLSelectElement>(
            '[data-labeler-type-picker]'
          )
          if (picker) {
            e.preventDefault()
            picker.focus()
          }
          break
        }
        case 'e':
        case 'E': {
          const input = document.querySelector<HTMLInputElement>(
            '[data-labeler-box-text]'
          )
          if (input) {
            e.preventDefault()
            input.focus()
          }
          break
        }
        case '0':
          e.preventDefault()
          onResetZoom()
          break
        case 'Escape':
          selectBox(null)
          setSelectedBoxIds(new Set())
          setIsDrawingMode(false)
          break
        default:
          break
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [
    labeler,
    stepSelection,
    toggleVerifiedSelected,
    toggleBoxVerifiedMany,
    selectedBoxId,
    selectedBoxIds,
    setSelectedBoxIds,
    visibleBoxes,
    isDrawingMode,
    setIsDrawingMode,
    isSeeded,
    selectBox,
    onResetZoom,
    syncNow,
    labeledBoxCount,
    compareMode,
    selectComparisonItem,
  ])

  return (
    <DrawerContainer data-labeler-drawer='true'>
      <DrawerHeader>
        <HeaderTitle>Labeling</HeaderTitle>
        <Mono
          title={pageId ?? undefined}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            direction: 'rtl',
            textAlign: 'left',
            fontSize: '0.68rem',
            opacity: 0.65,
          }}>
          {pageId ?? '(resolving…)'}
        </Mono>
        <Row style={{ flexWrap: 'nowrap' }}>
          {pageMeta?.rotation ? (
            <Muted title='BQ page rotation'>rot {pageMeta.rotation}°</Muted>
          ) : null}
          {pageMeta?.category && <Muted>{pageMeta.category}</Muted>}
          <IconButton
            title='Keyboard shortcuts'
            onClick={() => setShowHelp((v) => !v)}>
            ?
          </IconButton>
          <IconButton title='Close labeling mode' onClick={onClose}>
            ✕
          </IconButton>
        </Row>
      </DrawerHeader>

      {showHelp && (
        <HelpPanel>
          <Row style={{ justifyContent: 'space-between' }}>
            <HeaderTitle>Keyboard shortcuts</HeaderTitle>
            <IconButton title='Close' onClick={() => setShowHelp(false)}>
              ✕
            </IconButton>
          </Row>
          {HELP_SECTIONS.map((section) => (
            <HelpGroup key={section.title}>
              <Muted style={{ fontWeight: 600 }}>{section.title}</Muted>
              {section.rows.map(([key, desc]) => (
                <HelpRow key={key}>
                  <KeyHint style={{ minWidth: '7rem', textAlign: 'center' }}>
                    {key}
                  </KeyHint>
                  <Muted>{desc}</Muted>
                </HelpRow>
              ))}
            </HelpGroup>
          ))}
        </HelpPanel>
      )}

      <Section>
        {(seedLists.length > 0 || (runs?.length ?? 0) > 0) && (
          <Row>
            <Select
              style={{ flex: 1, minWidth: 0 }}
              title='Source to seed from and compare against — a source list is what fusion consumes; raw extractor runs are for run-vs-run checks'
              value={seedSource ?? ''}
              onChange={(e) => setSeedSource(e.target.value || null)}>
              {seedLists.length > 0 && (
                <optgroup label='Source lists'>
                  {seedLists.map((list) => (
                    <option key={list.id} value={`list:${list.id}`}>
                      {list.name} · {list.record_count} records
                    </option>
                  ))}
                </optgroup>
              )}
              {(runs?.length ?? 0) > 0 && (
                <optgroup label='Extractor runs (raw, this page)'>
                  {runs?.map((run) => (
                    <option
                      key={run.dagster_run_id}
                      value={`run:${run.dagster_run_id}`}>
                      {run.dagster_run_id.slice(0, 8)} · {run.tag_count} tags ·{' '}
                      {run.ended_at?.slice(0, 10) ?? '?'}
                    </option>
                  ))}
                </optgroup>
              )}
            </Select>
          </Row>
        )}
        <Row>
          <Button
            variant='inputRaised'
            size='sm'
            disabled={!predictions || predictions.length === 0}
            onClick={seed}
            title={
              isSeeded
                ? 'Re-seed: replaces untouched seeds, keeps confirmed + drawn'
                : 'Copy the predictions into an editable working copy'
            }>
            {isSeeded ? 'Re-seed' : 'Seed'} (
            {isLoadingPredictions ? '…' : (predictions?.length ?? 0)})
          </Button>
          <Button
            variant='inputRaised'
            size='sm'
            disabled={!isSeeded || unverifiedCount === 0}
            onClick={confirmAll}>
            Confirm all
          </Button>
          <Button
            variant={isDrawingMode ? 'default' : 'inputRaised'}
            size='sm'
            onClick={() => setIsDrawingMode(!isDrawingMode)}
            title='Arm draw mode (b), then Shift+drag on the page. Off: Shift+drag bulk-selects; on a selected box, drag edges to resize, Shift+drag inside to move.'>
            Draw
          </Button>
          <Button
            variant='inputRaised'
            size='sm'
            disabled={!isSeeded || boxes.length === 0}
            onClick={clear}
            title='Remove every box + rejection on this page'>
            Clear
          </Button>
          <Button
            variant={compareMode ? 'default' : 'inputRaised'}
            size='sm'
            disabled={!isSeeded || (predictions?.length ?? 0) === 0}
            onClick={() => setCompareMode(!compareMode)}
            title='Score the selected run against your labels as ground truth (read-only while on)'>
            {compareMode ? 'Exit compare' : 'Compare'}
          </Button>
        </Row>
        {!isSeeded && (predictions?.length ?? 0) > 0 && (
          <Muted>
            Showing {predictions?.length} read-only boxes from the source — Seed
            to edit.
          </Muted>
        )}
      </Section>

      {compareMode && (
        <Section>
          <Row>
            <Muted>IoU ≥</Muted>
            <input
              type='range'
              min={0.05}
              max={1}
              step={0.05}
              value={iouThreshold}
              style={{ flex: 1 }}
              onChange={(e) => setIouThreshold(Number(e.target.value))}
            />
            <Mono>{iouThreshold.toFixed(2)}</Mono>
          </Row>
          <Row>
            <label
              style={{
                fontSize: '0.75rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                cursor: 'pointer',
              }}
              title="Untouched seeds are the model's own output — scoring against them reads ~100%">
              <TinyCheck
                type='checkbox'
                checked={compareVerifiedOnly}
                onChange={(e) => setCompareVerifiedOnly(e.target.checked)}
              />
              Confirmed ground truth only
            </label>
          </Row>
          {comparison && (
            <>
              <Muted>
                GT {comparison.counts.gt} · Predicted{' '}
                {comparison.counts.predicted} · Matched{' '}
                {comparison.counts.matched} · Missed {comparison.counts.missed}{' '}
                · False pos {comparison.counts.falsePositives}
              </Muted>
              {comparison.counts.missed > 0 && (
                <Muted>
                  ↳ {comparison.counts.missedNotDetected} not detected,{' '}
                  {comparison.counts.missedUnclassified} unclassified
                </Muted>
              )}
              <MetricsGrid>
                <span />
                <Muted>Precision</Muted>
                <Muted>Recall</Muted>
                <Muted>F1</Muted>
                {(['box', 'text', 'type', 'kind'] as const).map((row) => (
                  <React.Fragment key={row}>
                    <Muted>{typeLabel(row)}</Muted>
                    <Mono>{pct(comparison.metrics[row].precision)}</Mono>
                    <Mono>{pct(comparison.metrics[row].recall)}</Mono>
                    <Mono>{pct(comparison.metrics[row].f1)}</Mono>
                  </React.Fragment>
                ))}
              </MetricsGrid>
              <Row>
                <CategoryChip
                  $active={compareCategoryFilter === null}
                  onClick={() => setCompareCategoryFilter(null)}>
                  All ({comparison.items.length})
                </CategoryChip>
                {COMPARISON_SEVERITY.map((cat) => {
                  const n = comparison.items.filter(
                    (i) => i.category === cat
                  ).length
                  if (n === 0) return null
                  return (
                    <CategoryChip
                      key={cat}
                      $active={compareCategoryFilter === cat}
                      onClick={() =>
                        setCompareCategoryFilter(
                          compareCategoryFilter === cat ? null : cat
                        )
                      }>
                      <Swatch $color={COMPARISON_COLORS[cat].stroke} />
                      {COMPARISON_COLORS[cat].label} ({n})
                    </CategoryChip>
                  )
                })}
              </Row>
            </>
          )}
        </Section>
      )}

      {compareMode && (
        <BoxList>
          {(comparison?.items ?? [])
            .filter(
              (i) =>
                !compareCategoryFilter || i.category === compareCategoryFilter
            )
            .map((item) => (
              <ComparisonRow
                key={item.id}
                $selected={item.id === selectedComparisonId}
                $color={COMPARISON_COLORS[item.category].stroke}
                onClick={() => selectComparisonItem(item.id)}>
                <Row>
                  <Muted>{COMPARISON_COLORS[item.category].label}</Muted>
                  {item.missKind && (
                    <Muted>
                      (
                      {item.missKind === 'unclassified'
                        ? 'unclassified'
                        : 'not detected'}
                      )
                    </Muted>
                  )}
                  {item.iou !== null && (
                    <Mono style={{ marginLeft: 'auto' }}>
                      IoU {item.iou.toFixed(3)}
                    </Mono>
                  )}
                </Row>
                {item.predictedText !== null && (
                  <Mono>
                    Pred: {item.predictedText || '(no text)'} [
                    {item.predictedType ?? 'untyped'}]
                  </Mono>
                )}
                {item.gtText !== null && (
                  <Mono>
                    GT: {item.gtText || '(no text)'} [{item.gtType ?? 'untyped'}
                    ]
                  </Mono>
                )}
              </ComparisonRow>
            ))}
          {comparison && comparison.items.length === 0 && (
            <Section>
              <Muted>Nothing to compare on this page.</Muted>
            </Section>
          )}
        </BoxList>
      )}

      {!compareMode && isSeeded && (
        <Section>
          <Row>
            <TextInput
              placeholder='Filter boxes…'
              value={boxTextFilter}
              onChange={(e) => setBoxTextFilter(e.target.value)}
            />
            <Select
              value={boxTypeFilter}
              onChange={(e) => setBoxTypeFilter(e.target.value)}>
              <option value='all'>All types</option>
              <option value='untyped'>Untyped</option>
              {Object.entries(typesByKind(taxonomy)).map(([kind, types]) => (
                <optgroup key={kind} label={typeLabel(kind)}>
                  {types.map((type) => (
                    <option key={type} value={type}>
                      {typeLabel(type)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
            <Select
              value={boxVerifiedFilter}
              onChange={(e) =>
                setBoxVerifiedFilter(
                  e.target.value as 'all' | 'verified' | 'unverified'
                )
              }>
              <option value='all'>All</option>
              <option value='verified'>Confirmed</option>
              <option value='unverified'>Unconfirmed</option>
            </Select>
          </Row>
          <Row>
            <Select
              value={boxDescFilter}
              onChange={(e) =>
                setBoxDescFilter(e.target.value as 'all' | 'has' | 'none')
              }>
              <option value='all'>Any description</option>
              <option value='has'>Has description</option>
              <option value='none'>No description</option>
            </Select>
            <label
              style={{
                fontSize: '0.72rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                cursor: 'pointer',
              }}>
              <TinyCheck
                type='checkbox'
                checked={showDescriptions}
                onChange={(e) => setShowDescriptions(e.target.checked)}
              />
              show descriptions
            </label>
          </Row>
          {boxes.length > 0 && (
            <div>
              <Row style={{ justifyContent: 'space-between' }}>
                <Muted>
                  {boxes.length} total
                  {untypedCount > 0 && ` (${untypedCount} untyped)`}
                  {visibleBoxes.length !== boxes.length &&
                    ` · ${visibleBoxes.length} shown`}
                </Muted>
                <Muted>
                  {boxes.length - unverifiedCount}/{boxes.length} verified
                </Muted>
              </Row>
              <ProgressTrack>
                <ProgressFill
                  $pct={
                    boxes.length > 0
                      ? ((boxes.length - unverifiedCount) / boxes.length) * 100
                      : 0
                  }
                />
              </ProgressTrack>
            </div>
          )}
          {selectedBoxIds.size > 0 && (
            <Row>
              <Muted>{selectedBoxIds.size} selected</Muted>
              <TypePicker value={null} onChange={retypeSelected} />
              <Button
                variant='inputRaised'
                size='sm'
                onClick={() => labeler.remove(selectedBoxIds)}>
                Delete
              </Button>
              {selectedBoxIds.size >= 2 && (
                <Button
                  variant='inputRaised'
                  size='sm'
                  title='Merge into one box: texts joined, bboxes unioned (g)'
                  onClick={() =>
                    setGroupDelimiter((cur) => (cur === null ? ' ' : cur))
                  }>
                  Group
                </Button>
              )}
              <Button
                variant='inputRaised'
                size='sm'
                onClick={() => setSelectedBoxIds(new Set())}>
                Clear selection
              </Button>
            </Row>
          )}
          {groupDelimiter !== null && selectedBoxIds.size >= 2 && (
            <Row>
              <Muted>Join {selectedBoxIds.size} texts with</Muted>
              <TextInput
                style={{ maxWidth: '5rem', flex: 'none' }}
                value={groupDelimiter}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    groupSelected(groupDelimiter)
                    setGroupDelimiter(null)
                  } else if (e.key === 'Escape') {
                    setGroupDelimiter(null)
                  }
                }}
                onChange={(e) => setGroupDelimiter(e.target.value)}
              />
              <Button
                variant='default'
                size='sm'
                onClick={() => {
                  groupSelected(groupDelimiter)
                  setGroupDelimiter(null)
                }}>
                Merge
              </Button>
              <Button
                variant='inputRaised'
                size='sm'
                onClick={() => setGroupDelimiter(null)}>
                Cancel
              </Button>
            </Row>
          )}
        </Section>
      )}

      {!compareMode && (
        <BoxList>
          {isSeeded ? (
            visibleBoxes.length > 0 ? (
              visibleBoxes.map((box) => (
                <BoxRow
                  key={box.id}
                  box={box}
                  showDescriptions={showDescriptions}
                />
              ))
            ) : (
              <Section>
                <Muted>
                  {boxes.length === 0
                    ? 'No boxes yet. Seed the predictions, or arm Draw (b) and Shift+drag on the page.'
                    : 'No boxes match the filters.'}
                </Muted>
              </Section>
            )
          ) : (
            <Section>
              <Muted>
                {isLoadingPredictions
                  ? 'Loading the source…'
                  : predictionsError
                    ? `The source failed to load — this is a server error, not an empty page. (${predictionsError})`
                    : (predictions?.length ?? 0) === 0
                      ? seedSource?.startsWith('list:')
                        ? 'The selected source list asserts nothing on this page. Pick another source above, or label by hand: arm Draw (b) and Shift+drag on the drawing.'
                        : siteStats && siteStats.tag_count === 0
                          ? `The extractor has never run on ${siteId} in this environment, so raw runs have nothing to seed anywhere on this site. Pick a source list above if one exists, or label by hand: arm Draw (b) and Shift+drag.`
                          : 'This run has no output for this page. You can still label it by hand: arm Draw (b) and Shift+drag on the drawing.'
                      : 'The source is read-only until seeded.'}
              </Muted>
            </Section>
          )}
        </BoxList>
      )}

      <Footer>
        <Row style={{ flexWrap: 'nowrap' }}>
          <Muted style={{ whiteSpace: 'nowrap' }}>
            Your list: {labeledBoxCount} boxes ·{' '}
            {sitePages.length === 1 ? '1 page' : `${sitePages.length} pages`}
          </Muted>
          <Row
            style={{
              marginLeft: 'auto',
              flexWrap: 'nowrap',
              minWidth: 0,
            }}>
            <SyncDot $state={syncStatus.state} />
            <Muted
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {isHydrating
                ? 'Loading your published list…'
                : syncStatus.state === 'syncing'
                  ? 'Syncing…'
                  : syncStatus.state === 'dirty'
                    ? 'Unsynced edits'
                    : syncStatus.state === 'error'
                      ? `Sync failed: ${syncStatus.error}`
                      : syncStatus.state === 'synced'
                        ? `Synced${syncStatus.lastSyncedAt ? ` · ${new Date(syncStatus.lastSyncedAt).toLocaleTimeString()}` : ''}`
                        : 'syncs automatically'}
            </Muted>
            <Button
              variant='inputRaised'
              size='sm'
              disabled={labeledBoxCount === 0 || syncStatus.state === 'syncing'}
              onClick={() => void syncNow()}
              title='Sync now (Ctrl/Cmd+S) — edits also sync automatically a few seconds after you stop'>
              Sync now
            </Button>
          </Row>
        </Row>
      </Footer>
    </DrawerContainer>
  )
}
